/**
 * Cabochon tiles are laid from two kinds of image: the octagons and the small
 * squares at their corners. A variant stores them as one plank list, octagons
 * first then squares, the same number of each — the seamless worker splits the
 * list in half on save (seamless-generator worker.py), so the editor must too.
 */

export const isCabochonPattern = (pattern: unknown): boolean =>
  typeof pattern === 'string' && pattern.toLowerCase().replace(/[\s_-]+/g, '') === 'cabochon';

/**
 * The corner square, from a size whose width is the octagon. It is laid turned
 * 45° over the corners where four octagons meet; for a regular octagon its side
 * is the octagon's side, width / (1 + √2). A height below the width is that
 * square given explicitly ("20x7"); otherwise ("20x20", how cabochon tiles are
 * sold) the height is the octagon's own. Same rule as the seamless worker.
 */
export const cabochonSquareCm = (octagonCm: number, heightCm: number): number =>
  heightCm > 0 && heightCm < octagonCm ? heightCm : Math.round((octagonCm / (1 + Math.SQRT2)) * 100) / 100;

/** Octagons are the first half of the list, squares the second (one image serves both). */
export function splitCabochonImages<T>(images: T[]): { octagons: T[]; squares: T[] } {
  const mid = Math.floor(images.length / 2) || 1;
  return {
    octagons: images.slice(0, mid),
    squares: mid < images.length ? images.slice(mid) : images.slice(0, mid),
  };
}

/** The half split only reads the list back right when both kinds have the same count. */
export const isCabochonBalanced = (octagons: unknown[], squares: unknown[]): boolean =>
  octagons.length > 0 && octagons.length === squares.length;

// A joined list no longer says where the octagons stop, so a list joined with
// unequal counts (one the half split would misread) is remembered here.
const unbalancedLists = new WeakSet<object>();

export function joinCabochonImages<T>(octagons: T[], squares: T[]): T[] {
  const joined = [...octagons, ...squares];
  if (joined.length > 0 && !isCabochonBalanced(octagons, squares)) unbalancedLists.add(joined);
  return joined;
}

/** True for a list joined with unequal octagon and square counts (lists loaded from a product are balanced). */
export const isUnbalancedCabochonList = (images: unknown[] | undefined): boolean =>
  !!images && unbalancedLists.has(images);
